/**
 * 📊 DRIVER PERFORMANCE SERVICE
 * Manages driver performance metrics from auto-cancellation system
 * Backend: /api/v1/admin/drivers/:driverId/performance
 */

import { getApiBaseUrl } from '@/config/environment';


export interface DriverPerformance {
  acceptanceRate: number; // Percentage (0-100)
  completionRate: number; // Percentage (0-100)
  cancellationRate: number; // Percentage (0-100)
  avgResponseTime: number; // Minutes
}

export interface DriverEarnings {
  total: number; // Net to driver
  thisMonth: number;
  avgBookingValue: number;
  breakdown?: {
    grossTotal: number; // Total booking amount
    platformFee: number; // 20% cut
    vat: number; // 7.5% tax
    netTotal: number; // What driver gets
  };
}

export interface RequestsBreakdown {
  total: number;
  accepted: number;
  declined: number;
  missed: number;
}

export interface DayTrend {
  date: string; // "YYYY-MM-DD"
  bookings: number;
  completed: number;
}

export interface PerformanceResponse {
  success: boolean;
  data?: {
    performance: DriverPerformance;
    earnings: DriverEarnings;
    requests: RequestsBreakdown;
    trends: {
      last30Days: DayTrend[];
    };
  };
  error?: string;
}

class DriverPerformanceService {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${getApiBaseUrl()}${endpoint}`;
    const token = localStorage.getItem('token');

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Get performance metrics for a driver
   */
  async getPerformance(driverId: string): Promise<PerformanceResponse> {
    try {
      const response = await this.request<PerformanceResponse>(
        `/api/v1/admin/drivers/${driverId}/performance`
      );

      return response;
    } catch (error: any) {
      console.error('Failed to get driver performance:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get performance rating based on thresholds
   */
  getAcceptanceRating(rate: number): { color: string; label: string } {
    if (rate >= 80) return { color: '#4caf50', label: 'Good' };
    if (rate >= 60) return { color: '#ff9800', label: 'Fair' };
    return { color: '#f44336', label: 'Poor' };
  }

  getCompletionRating(rate: number): { color: string; label: string } {
    if (rate >= 95) return { color: '#4caf50', label: 'Excellent' };
    if (rate >= 85) return { color: '#ff9800', label: 'Good' };
    return { color: '#f44336', label: 'Needs Improvement' };
  }

  getResponseTimeRating(minutes: number): { color: string; label: string } {
    if (minutes <= 30) return { color: '#4caf50', label: 'Fast' };
    if (minutes <= 60) return { color: '#ff9800', label: 'Moderate' };
    return { color: '#f44336', label: 'Slow' };
  }

  getMissedRequestsRating(percentage: number): { color: string; label: string } {
    if (percentage <= 5) return { color: '#4caf50', label: 'Acceptable' };
    if (percentage <= 10) return { color: '#ff9800', label: 'High' };
    return { color: '#f44336', label: 'Critical' };
  }
}

export default new DriverPerformanceService();
